// SSRS to Amazon Quick page content. Same shape as domo.js; rendered by
// PlatformMigrationContent (components/BiPlatformPage).
export const ssrsPage = {
  whyMove: {
    eyebrow: 'Why move',
    title: 'Why teams move from SSRS to Amazon Quick',
    intro: 'The reasons we hear most from SQL Server Reporting Services customers.',
    cards: [
      { title: 'Retire the report servers', description: 'No more Windows servers, ReportServer databases, or SQL Server licenses kept around just for reporting.' },
      { title: 'Paginated and interactive in one place', description: 'Pixel-perfect paginated reports and interactive dashboards run side by side on Quick, on the same data.' },
      { title: 'AI agents on the same data', description: 'Once reports are on Quick, Chat Agents, Flows, and Research work on the same governed datasets.' },
      { title: 'Security in one place', description: 'Folder and item permissions move to IAM, SSO, and row-level security on AWS.' },
    ],
  },

  metadata: {
    id: 'metadata',
    eyebrow: 'Built on your SSRS metadata',
    title: 'We read your report server catalog directly',
    intro: 'Instead of opening reports one by one, we pull metadata straight from the SSRS catalog and REST API. Discovery takes days, every decision traces back to real usage, and the same metadata drives conversion, testing, and training.',
    columns: ['What we pull', 'From', 'How we use it'],
    rows: [
      { what: 'Reports, folders, and linked reports', from: 'SSRS REST API and ReportServer catalog', use: 'Build the full inventory and group content by folder and business area' },
      { what: 'RDL definitions: layout, datasets, and expressions', from: 'Report definitions (RDL)', use: 'Rebuild as paginated reports and convert expressions to calculated fields' },
      { what: 'Report parameters and cascading prompts', from: 'Report definitions (RDL)', use: 'Recreate as Quick parameters and controls' },
      { what: 'Shared data sources and datasets', from: 'SSRS REST API', use: 'Plan the move of SQL Server sources to Amazon RDS or Redshift and map connectivity' },
      { what: 'Subscriptions and schedules', from: 'SSRS REST API and catalog', use: 'Move to scheduled email reports with the same recipients and timing' },
      { what: 'Execution history', from: 'ExecutionLog3', use: 'Find the most-used and unused reports to rank waves and retire dead content' },
      { what: 'Folder and item permissions', from: 'SSRS REST API', use: 'Map to SSO and IAM groups and recreate access on Amazon Quick' },
    ],
    benefits: {
      eyebrow: 'What you get from it',
      items: [
        { title: 'Full inventory and lineage', description: 'Every report traced back through its datasets and stored procedures to the source tables.' },
        { title: 'Complexity score per report', description: 'Expressions, subreports, and parameters scored, so each wave is sized realistically.' },
        { title: 'Wave plan from real usage', description: 'Waves ranked by the execution log, not by who asks loudest.' },
        { title: 'UAT test cases, generated', description: 'Expected values pulled from SSRS and compared with Quick automatically.' },
      ],
      note: {
        lead: 'Read-only access.',
        text: 'Your DBA grants a read-only login to the ReportServer catalog and a browse-only SSRS role. Nothing in SSRS changes until you retire it.',
      },
    },
  },

  process: {
    eyebrow: 'How an SSRS migration runs',
    title: 'Automated and AI-led, wave by wave',
    intro: 'Steps 3 to 5 repeat for each wave, so your team reviews exceptions instead of testing every report.',
    steps: [
      { tag: 'Automated', title: 'Automated assessment', description: 'Metadata pulled from the SSRS catalog and REST API: reports, RDL, parameters, data sources, subscriptions, and usage.' },
      { tag: 'AI-led', title: 'Wave planning', description: 'Reports grouped into waves by usage, shared data sources, and folder. Unused reports are flagged to retire.' },
      { tag: 'Automated', title: 'Automated conversion', description: 'RDL rebuilt as paginated reports, expressions converted to calculated fields, and subscriptions moved to schedules.' },
      { tag: 'AI-led', title: 'AI-led UAT and QA', description: 'Every migrated report compared with its SSRS original, page by page. Your team signs off on exceptions only.' },
      { tag: 'Automated', title: 'Enablement and training', description: 'Training per wave, built from who runs and subscribes to which reports today.' },
    ],
  },

  afterMigration: {
    eyebrow: 'After the migration',
    title: 'More than reports: AI on the same data',
    items: [
      { title: 'Ask the data', description: 'A Chat Agent answers questions on the same datasets your old SSRS reports used, with sources.' },
      { title: 'Automate the follow-up', description: 'Quick Flows turn a threshold alert into a task, an email, or a ticket.' },
      { title: 'Research with context', description: 'Quick Research blends your internal numbers with outside sources in one report.' },
    ],
  },

  showFunding: true,
};
